/**
 * Italian Codice Fiscale Parser and Validator
 * Extracts birth date, birth place, and validates coherence with name/surname
 * Uses complete Italian municipality database (7904 codes)
 */

import comuniData from "@/data/comuni.json";

// Codice Fiscale month mapping
const MONTH_CODES = {
  'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'H': 6,
  'L': 7, 'M': 8, 'P': 9, 'R': 10, 'S': 11, 'T': 12
};

// Foreign countries (Z codes) — not in comuni.json
const FOREIGN_CODES = {
  'Z100': { name: 'Albania', province: 'EE' },
  'Z103': { name: 'Francia', province: 'EE' },
  'Z104': { name: 'Germania', province: 'EE' },
  'Z107': { name: 'Grecia', province: 'EE' },
  'Z109': { name: 'Irlanda', province: 'EE' },
  'Z112': { name: 'Malta', province: 'EE' },
  'Z114': { name: 'Olanda', province: 'EE' },
  'Z115': { name: 'Polonia', province: 'EE' },
  'Z116': { name: 'Portogallo', province: 'EE' },
  'Z118': { name: 'Romania', province: 'EE' },
  'Z120': { name: 'Spagna', province: 'EE' },
  'Z127': { name: 'Svizzera', province: 'EE' },
  'Z129': { name: 'Ucraina', province: 'EE' },
  'Z131': { name: 'Ungheria', province: 'EE' },
  'Z132': { name: 'Bulgaria', province: 'EE' },
  'Z133': { name: 'Rep. Ceca', province: 'EE' },
  'Z138': { name: 'Croazia', province: 'EE' },
  'Z139': { name: 'Slovenia', province: 'EE' },
  'Z140': { name: 'Slovacchia', province: 'EE' },
  'Z144': { name: 'Russia', province: 'EE' },
  'Z148': { name: 'Serbia', province: 'EE' },
  'Z150': { name: 'Bosnia-Erzeg.', province: 'EE' },
  'Z153': { name: 'Lituania', province: 'EE' },
  'Z154': { name: 'Lettonia', province: 'EE' },
  'Z155': { name: 'Estonia', province: 'EE' },
  'Z158': { name: 'Macedonia', province: 'EE' },
  'Z159': { name: 'Kosovo', province: 'EE' },
  'Z200': { name: 'Marocco', province: 'EE' },
  'Z208': { name: 'Egitto', province: 'EE' },
  'Z210': { name: 'Etiopia', province: 'EE' },
  'Z215': { name: 'Tunisia', province: 'EE' },
  'Z219': { name: 'Nigeria', province: 'EE' },
  'Z229': { name: 'Senegal', province: 'EE' },
  'Z233': { name: 'Ghana', province: 'EE' },
  'Z243': { name: 'Costa d\'Avorio', province: 'EE' },
  'Z302': { name: 'Cina', province: 'EE' },
  'Z310': { name: 'India', province: 'EE' },
  'Z330': { name: 'Pakistan', province: 'EE' },
  'Z335': { name: 'Bangladesh', province: 'EE' },
  'Z340': { name: 'Filippine', province: 'EE' },
  'Z344': { name: 'Sri Lanka', province: 'EE' },
  'Z352': { name: 'Iran', province: 'EE' },
  'Z404': { name: 'Giappone', province: 'EE' },
  'Z505': { name: 'Australia', province: 'EE' },
  'Z401': { name: 'Canada', province: 'EE' },
  'Z404': { name: 'Stati Uniti', province: 'EE' },
  'Z511': { name: 'Moldavia', province: 'EE' },
  'Z600': { name: 'Argentina', province: 'EE' },
  'Z602': { name: 'Brasile', province: 'EE' },
  'Z608': { name: 'Colombia', province: 'EE' },
  'Z611': { name: 'Ecuador', province: 'EE' },
  'Z612': { name: 'Peru', province: 'EE' },
  'Z614': { name: 'Venezuela', province: 'EE' },
};

/**
 * Validate Codice Fiscale format
 */
export function isValidCFFormat(cf) {
  if (!cf || typeof cf !== 'string') return false;
  const cleaned = cf.toUpperCase().replace(/\s/g, '');
  // Standard format: 16 characters
  return /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(cleaned);
}

/**
 * Extract birth date from Codice Fiscale
 * Returns { year, month, day, gender } or null if invalid
 */
export function extractBirthDateFromCF(cf) {
  if (!isValidCFFormat(cf)) return null;
  
  const cleaned = cf.toUpperCase();
  
  try {
    // Year: positions 6-7
    let yearCode = parseInt(cleaned.substring(6, 8), 10);
    // Determine century (assume 1900s if > 30, 2000s otherwise)
    const currentYear = new Date().getFullYear() % 100;
    const year = yearCode > currentYear ? 1900 + yearCode : 2000 + yearCode;
    
    // Month: position 8
    const monthCode = cleaned.charAt(8);
    const month = MONTH_CODES[monthCode];
    if (!month) return null;
    
    // Day: positions 9-10 (females have +40)
    let dayCode = parseInt(cleaned.substring(9, 11), 10);
    const gender = dayCode > 40 ? 'F' : 'M';
    const day = dayCode > 40 ? dayCode - 40 : dayCode;
    
    // Validate date
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }
    
    return {
      year,
      month,
      day,
      gender,
      formatted: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`
    };
  } catch (e) {
    return null;
  }
}

/**
 * Extract birth place from Codice Fiscale
 * Returns { code, name, province } or null if not found
 */
export function extractBirthPlaceFromCF(cf) {
  if (!isValidCFFormat(cf)) return null;
  
  const cleaned = cf.toUpperCase();
  const placeCode = cleaned.substring(11, 15);
  
  // Check complete municipality database (7904 codes)
  const comuneEntry = comuniData[placeCode];
  if (comuneEntry) {
    return {
      code: placeCode,
      name: comuneEntry.n,
      province: comuneEntry.s
    };
  }
  
  // Check foreign countries (Z codes)
  const foreign = FOREIGN_CODES[placeCode];
  if (foreign) {
    return {
      code: placeCode,
      name: foreign.name,
      province: foreign.province
    };
  }
  
  // Return code even if not found
  return {
    code: placeCode,
    name: null,
    province: null
  };
}

/**
 * Generate consonants/vowels extraction for CF name matching
 */
function extractConsonants(str) {
  return str.toUpperCase().replace(/[^A-Z]/g, '').replace(/[AEIOU]/g, '');
}

function extractVowels(str) {
  return str.toUpperCase().replace(/[^AEIOU]/g, '');
}

/**
 * Generate expected CF code for surname (first 3 chars)
 */
function generateSurnameCode(surname) {
  const consonants = extractConsonants(surname);
  const vowels = extractVowels(surname);
  const combined = consonants + vowels + 'XXX';
  return combined.substring(0, 3);
}

/**
 * Generate expected CF code for name (positions 3-5)
 */
function generateNameCode(name) {
  const consonants = extractConsonants(name);
  
  if (consonants.length >= 4) {
    // For names with 4+ consonants, take 1st, 3rd, 4th
    return consonants[0] + consonants[2] + consonants[3];
  } else if (consonants.length === 3) {
    return consonants;
  } else {
    const vowels = extractVowels(name);
    const combined = consonants + vowels + 'XXX';
    return combined.substring(0, 3);
  }
}

/**
 * Check if Codice Fiscale is coherent with given name and surname
 * Returns { coherent: boolean, message: string }
 *
 * @param {string} cf         — codice fiscale
 * @param {string} fullName   — nome completo (fallback quando lastName/firstName mancano)
 * @param {string} [lastName] — cognome esplicito (preferito, gestisce cognomi
 *                              composti con spazio: "DI GIOVANNI", "DE MARCO",
 *                              "LO STORTO", "DEL VECCHIO", ecc.)
 * @param {string} [firstName]— nome esplicito
 */
export function checkCFCoherence(cf, fullName, lastName, firstName) {
  if (!cf) {
    return { coherent: true, message: '' };
  }

  if (!isValidCFFormat(cf)) {
    return { coherent: false, message: 'Formato Codice Fiscale non valido' };
  }

  const cleaned = cf.toUpperCase();
  const cfSurnameCode = cleaned.substring(0, 3);
  const cfNameCode = cleaned.substring(3, 6);

  // Percorso PREFERITO (17/07/2026 — v1.4.1): quando abbiamo cognome e
  // nome esplicitamente separati usiamo QUELLI, così i cognomi/nomi con
  // uno spazio interno (DI GIOVANNI, DE MARCO, LO STORTO, DEL VECCHIO,
  // MARIA GRAZIA, ANNA LUISA, …) sono trattati come un'unica stringa.
  const explicitLast = (lastName || '').trim();
  const explicitFirst = (firstName || '').trim();
  if (explicitLast && explicitFirst) {
    const expectedSurname = generateSurnameCode(explicitLast);
    const expectedName = generateNameCode(explicitFirst);
    if (cfSurnameCode === expectedSurname && cfNameCode === expectedName) {
      return { coherent: true, message: '' };
    }
    return {
      coherent: false,
      message: 'Il Codice Fiscale potrebbe non corrispondere al nome inserito'
    };
  }

  // Fallback legacy: solo fullName disponibile. Prova entrambi gli
  // ordinamenti "Nome Cognome" e "Cognome Nome".
  if (!fullName) {
    return { coherent: true, message: '' };
  }
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) {
    return { coherent: true, message: '' }; // Can't validate with single name
  }

  // Try "Nome Cognome" format
  let surname = parts[parts.length - 1];
  let name = parts.slice(0, -1).join(' ');
  let expectedSurname = generateSurnameCode(surname);
  let expectedName = generateNameCode(name);
  if (cfSurnameCode === expectedSurname && cfNameCode === expectedName) {
    return { coherent: true, message: '' };
  }

  // Try "Cognome Nome" format
  surname = parts[0];
  name = parts.slice(1).join(' ');
  expectedSurname = generateSurnameCode(surname);
  expectedName = generateNameCode(name);
  if (cfSurnameCode === expectedSurname && cfNameCode === expectedName) {
    return { coherent: true, message: '' };
  }

  return {
    coherent: false,
    message: 'Il Codice Fiscale potrebbe non corrispondere al nome inserito'
  };
}

/**
 * Full CF analysis - returns all extracted info
 *
 * @param {string} cf
 * @param {string} [fullName]  — fallback legacy
 * @param {string} [lastName]  — cognome esplicito (preferito per cognomi composti)
 * @param {string} [firstName] — nome esplicito
 * @param {string} [birthPlaceInput] — comune di nascita inserito dall'operatore
 *                                      (per il check di consistenza CF↔città)
 */
export function analyzeCF(cf, fullName = '', lastName = '', firstName = '', birthPlaceInput = '') {
  const result = {
    valid: false,
    birthDate: null,
    birthPlace: null,
    gender: null,
    coherence: { coherent: true, message: '' },
    // 17/07/2026 — v1.4.1: consistenza fra il codice catastale nel CF
    // (posizioni 11-14) e il Comune di Nascita inserito manualmente
    // dall'operatore. Utile per catturare typo su clienti storici
    // (es. CF Roma ma anagrafica scrive "Roman" o "Rimini").
    //   status: 'match' | 'mismatch' | 'unknown' | 'skip'
    //     - match    → nome comune inserito coincide con quello del CF
    //     - mismatch → i due nomi sono diversi (warning ambra)
    //     - unknown  → codice catastale non trovato in DB (nessun check)
    //     - skip     → operatore non ha ancora inserito il comune
    birthPlaceCheck: { status: 'skip', message: '' },
  };
  
  if (!isValidCFFormat(cf)) {
    return result;
  }
  
  result.valid = true;
  
  const birthInfo = extractBirthDateFromCF(cf);
  if (birthInfo) {
    result.birthDate = birthInfo.formatted;
    result.gender = birthInfo.gender;
  }
  
  const placeInfo = extractBirthPlaceFromCF(cf);
  if (placeInfo && placeInfo.name) {
    result.birthPlace = `${placeInfo.name} (${placeInfo.code}) (${placeInfo.province})`;
  }
  
  if (fullName || (lastName && firstName)) {
    result.coherence = checkCFCoherence(cf, fullName, lastName, firstName);
  }

  // Check consistenza Comune Nascita ↔ codice catastale CF.
  result.birthPlaceCheck = checkBirthPlaceCoherence(cf, birthPlaceInput, placeInfo);
  
  return result;
}


/**
 * Normalize a Comune string for tolerant comparison:
 *   - lowercase, no diacritics/accents
 *   - strip trailing " (XXXX)" catastal code, " (RM)" province suffix
 *   - collapse whitespace
 */
function normalizeComune(str) {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // remove accents
    .replace(/\s*\([^)]*\)/g, '')  // strip "(C773)", "(RM)" suffixes
    .replace(/[\s'.\-,]+/g, ' ')
    .trim();
}


/**
 * Check consistency between the catasto code embedded in the CF and
 * the "Comune di Nascita" typed by the operator.
 *
 * Returns { status, message, expectedName? }
 *   status:
 *     - 'skip'    → CF format invalid OR operator hasn't typed anything yet
 *     - 'unknown' → catasto code not in DB → cannot verify
 *     - 'match'   → operator input matches the CF's comune (case+accents tolerant)
 *     - 'mismatch'→ operator input differs → likely typo
 */
export function checkBirthPlaceCoherence(cf, userInput, precomputedPlaceInfo = null) {
  if (!isValidCFFormat(cf)) {
    return { status: 'skip', message: '' };
  }
  const trimmed = (userInput || '').trim();
  if (!trimmed) {
    return { status: 'skip', message: '' };
  }
  const info = precomputedPlaceInfo || extractBirthPlaceFromCF(cf);
  if (!info || !info.name) {
    return {
      status: 'unknown',
      message: 'Codice catastale non riconosciuto — impossibile verificare il Comune di Nascita',
    };
  }
  const expected = normalizeComune(info.name);
  const provided = normalizeComune(trimmed);
  if (!expected || !provided) {
    return { status: 'skip', message: '' };
  }
  // Match esatto o "il comune inserito contiene quello atteso"
  // (l'operatore può aver scritto "Civitavecchia (C773) (RM)" e va bene).
  if (provided === expected || provided.startsWith(expected + ' ') || provided.includes(' ' + expected)) {
    return {
      status: 'match',
      message: `Comune verificato: ${info.name} (${info.code}) (${info.province})`,
      expectedName: info.name,
    };
  }
  return {
    status: 'mismatch',
    message: `Il Comune inserito non corrisponde al codice catastale del CF (atteso: ${info.name})`,
    expectedName: info.name,
  };
}
